import { autoinject, BindingEngine, computedFrom, transient } from "aurelia-framework";
import { BusinessUnitModel, CustomerModel, CustomerStepEnum } from "../../../../models/customers/customer.models";
import { StepModel, ClaimsRequireTypeEnum } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { CustomerBasic } from "./steps/basic/customer-basic";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { CustomerRoles } from "./steps/roles/roles";
import { ViewPeople } from "../../settings/view-people";
import { I18N } from "aurelia-i18n";
import { activationStrategy, Router } from "aurelia-router";
import { BusinessUnitRuleConfigs } from "./steps/rules/rule-configs";
import { CustomerService } from "../../../../services/modules/customer-client.service";
import { BusinessUnit } from "./steps/business-units/business-units";
import { Reflection } from "../../../../utilities/reflection";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { Guid } from "../../../../utilities/guid";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { RightshipConfirmationDialogModel } from "../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CustomerRuleParameters } from "./steps/rule-parameters/customer-rule-parameters";
import { MenuItem } from "../../../components/menu/menu-item";
import { SharedActivities } from "./steps/shared-activity/shared-activities";
import { CustomerRuleHistoryTrail } from "./steps/history-trail/customer-rule-history-trail";
import { CaSettings } from "./steps/ca-settings/ca-settings";
import { RuleRiskConfigs } from "./steps/rules/rule-risk-configs";
import { CustomerReportsSettings } from "./steps/customer-reports-settings/customer-reports-settings";
import { CustomerSubscriptionModel } from "../../../../models/customers/customer-subscription.models";
import { CustomerSubscriptions } from "./steps/customer-subscriptions/customer-subscriptions";
import { CustomerSanctions } from "./steps/customer-sanctions/customer-sanctions";
import { SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { CustomerFormsSetting } from "./steps/customer-forms-setting/customer-forms-setting";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { CustomerDueDiligenceConfiguration } from "@rightship-tech/due-diligence-app/views/pages/customer-due-diligence-configuration/customer-due-diligence-configuration";

interface CustomerDetailsModel {
    id: string | number,
    parentCustomerId: string | number,
    customer: CustomerModel,
}

interface RolesModel {
}

interface PeopleModel {
    customerId: number,
    branchId: number,
    oDataUrl: string,
    filterByIsActive: boolean,
    businessUnits: BusinessUnitModel[],
}

interface RulesModel {
    id: number,
    branchId: number,
}


@autoinject()
@transient()
export class Customer {

    private _tabs: StepModel[] = [];
    private ruleConfigParameters: LookupValue[] = [];

    public item: CustomerModel;
    public viewModelInCompose: any;
    public modelInCompose: CustomerDetailsModel | RolesModel | PeopleModel | RulesModel = {};
    public uow: UnitOfWorkObserver;
    public currentTab: StepModel = null;
    public originalCustomerSubscription: CustomerSubscriptionModel;
    parameterIdsUsed: number[];

    public get isExistingCustomer() {
        return this.item && this.item.customerId > 0;
    }

    private args: any;

    get branchId() {
        return this.item && this.item.branchId
            ? this.item.branchId
            : this.userClientService.currentUser.branchId;
    }

    @computedFrom(Reflection.getPropertyPath<Customer>(c => c._tabs))
    get tabs(): StepModel[] {
        return this._tabs;
    }

    public canAdd: boolean = false;
    public canEdit: boolean = false;
    public canDelete: boolean = false;
    public isExporting: boolean = false;

    constructor(private customerBasicViewModel: CustomerBasic, private bindingEngine: BindingEngine,
        private userClientService: UserClientService, private notificationClientService: NotificationClientService,
        private userClaimsService: UserClaimsService, private validationsClientService: ValidationsClientService,
        private customerRolesViewModel: CustomerRoles, private router: Router, private lookupValueService: LookupClientService,
        private BusinessUnitRuleConfigsViewModel: BusinessUnitRuleConfigs,
        private ruleRiskConfigs: RuleRiskConfigs,
        private sharedActivitiesViewModel: SharedActivities,
        private customerReportsSettings: CustomerReportsSettings,
        private customerFormsSetting: CustomerFormsSetting,
        private customerDueDiligenceSettings: CustomerDueDiligenceConfiguration,
        private dialogClientService: DialogClientService,
        private viewPeople: ViewPeople, private customerRuleParameters: CustomerRuleParameters,
        private customerRuleHistoryTrail: CustomerRuleHistoryTrail,
        private emissionsPerformanceTarget: CaSettings,
        private i18N: I18N,
        private customerService: CustomerService,
        private customerSubscriptionsViewModel: CustomerSubscriptions,
        private customerSanctionsViewModel: CustomerSanctions,
        private businessUnitViewModel: BusinessUnit,
        private signalService: SignalClientService) {

        //Claims
        this.canAdd = userClaimsService.hasPermission(Claims.CanAddCustomers);
        this.canEdit = userClaimsService.hasPermission(Claims.CanUpdateCustomers);
        this.canDelete = userClaimsService.hasPermission(Claims.CanDeleteCustomers);
    }

    async activate(args: any) {
        this.args = args;
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RuleConfigParameters;
        const ruleConfigParameters = await this.lookupValueService.getLookup(request);
        this.ruleConfigParameters = ruleConfigParameters.activeValues;
        await this.getItem(this.args.id);
        const availabletabs = this.tabs.filter(x => typeof (x.show) == "function" ? x.show() : x.show);

        if (window.history && window.history.state && Object.keys(window.history.state).indexOf("current-tab") > -1) {
            const step = window.history.state["current-tab"];
            const tabs = availabletabs.filter(x => x.step == step);

            if (tabs && tabs.length > 0) {
                this.currentTab = tabs[0];
            }
        }

        if (args && args.tabStep) {
            this.currentTab = availabletabs.filter(x => x.step == args.tabStep)[0];
        }

        if (this.currentTab && this.currentTab.onClick)
            this.currentTab.onClick(null);
        else availabletabs[0].onClick(null);
    
        this.signalService.subscribe(SignalFunctions.customerActiveStateChanged , this.onCustomerActiveStateChanged.bind(this));
    }

    async onCustomerActiveStateChanged(data: {customerId: number, businessUnitIds: number[] }) 
    {
        if(data && data.customerId == this.item.customerId)
        {
            this.customerService.clearCustomerAndBusinessUnitCache(data.customerId, data.businessUnitIds);
            //reload customer if no changes
            if(!this.areThereUnsavedChanges())
            {
                await this.getItem(this.item.customerId);
            }
        }
    }


    async canDeactivate() {
        if (this.areThereUnsavedChanges()) {
            const result = await new Promise<boolean>((resolve) => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.LeaveWithoutSavingDialog, {
                    save: () => {
                        this.save().then(() => {
                            resolve(true);
                        }).catch(() => {
                            resolve(false);
                        });
                    },
                    cancel: () => {
                        resolve(true);
                    },
                    close: () => {
                        resolve(false);
                    }
                }, true);

            });
            return result;
        }
    }

    determineActivationStrategy() {
        return activationStrategy.replace;
    }

    protected getItem(id: number): Promise<CustomerModel> {
        return new Promise<CustomerModel>((resolve, reject) => {
            if (id > 0) {

                this.customerService.getCustomer(id).then(getCustomerResponse => {

                    this.parameterIdsUsed = [];
                    this.item = CloneHelper.cloneObject(getCustomerResponse.customer);

                    //Clear changes
                    this.clearChanges(() => {
                        this.updateTabs();
                        resolve(this.item);
                    });
                }, reject);                
            } else {
                this.item = {
                    entityType: EntityTypes.Customer,
                    identifier: Guid.generateGuid(),
                    name: null,
                    shouldPassToRightShipVettingQueue: false,
                    shouldAddVesselsToVettingList: null,
                    branchId: 0,
                    customerId: 0,
                    isActive: null,
                    isHosted: null,
                    isRuleAutomationEnabled: null,
                    businessUnits: [],
                    customerVettingLists: [],
                    customerSirePurchaserNames: [],
                    customerDomains: [],
                    code: null,
                    isDataSubmission: null,
                    defaultVoyageIncludesBallastLeg: null,
                    useMfa: null,
                    isMepTrial: null,
                    mepTrialExpiryDate: null,
                    isVettingCustomer: null,
                    canInvoice: null,
                    windwardClientId: null,
                    windwardClientSecret: null,
                    windwardVoiClientId: null,
                    windwardVoiClientSecret: null,
                    windwardVoiId: null,
                    windwardInProgressVoiId: null,
                    refinitiveGroupId: null,
                    refinitiveOverrideDefault: false
                };
                
                this.clearChanges(() => {
                    this.item.isActive = true;
                    this.item.isDataSubmission = false;
                    this.item.useMfa = true;
                    this.item.isRuleAutomationEnabled = true;
                    this.item.isVettingCustomer = false;
                    this.item.canInvoice = false;
                    this.updateTabs();
                    resolve(this.item);
                });
            }
        });
    }

    public clearChanges(callback: () => void = null) {
        if (this.uow) {
            this.uow.clearChanges();
            this.uow.dispose();
        }
        setTimeout(() => {
            this.uow = new UnitOfWorkObserver(this.bindingEngine, this.item);
            this.uow.startTracking();
            if (callback)
                callback();
        });
    }

    public goBack() {
        this.router.navigateBack();
    }

    areThereUnsavedChanges() {
        const changes = this.uow.getChanges();
        return (this.uow && this.uow.hasChanges() && !(Object.keys(changes).length == 1 && changes['identifier'])) ||
            this.customerReportsSettings.hasChanges() || this.customerSanctionsViewModel.hasChanges() || this.customerBasicViewModel.hasChanges();
    }

    private async updateCustomer(): Promise<boolean> {
        if (this.areThereUnsavedChanges()) {
            await this.showDeactivationWarning();
            return true;
        }
        return false;
    }


    protected async showDeactivationWarning() {
        const allChanges = this.uow.getChanges();
        if(Object.keys(allChanges).indexOf(Reflection.getPropertyName<CustomerModel>(x => x.isActive)) > -1 && !this.item.isActive) 
        {
            const model = new RightshipConfirmationDialogModel();
            model.confirm = this.i18N.tr('confirm-text');
            model.cancel = this.i18N.tr('cancel-text');

            model.messages.push('Deactivating this customer will also deactivate active business units and users. Do you want to continue? ');

            model.title = 'Deactivate Customer';

            model.onConfirm = () => this.saveInternal();
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
        }
        else 
        {
            await this.saveInternal();
        }
    }
    
    private saveInternal()
    {
        return new Promise<void>((resolve, reject) => {
            const isNew = !this.item.customerId;
            const allChanges = this.uow.getChanges();
            this.customerService.saveCustomer({
                id: this.item.customerId,
                changes: allChanges
            }).then(x => {
                this.customerReportsSettings.saveCustomerReports().then(() => {
                    if (this.customerReportsSettings.isFormValid) {
                        this.notificationClientService.show(this.i18N.tr('changes-saved-successfully-text'), "", NotificationType.Success, 5000);
                        this.item.customerId = x.generatedId;
                        this.clearChanges(() => {
                            this.updateTabs();
                        });
                        if (isNew) {
                            resolve();
                            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.UpdateCustomer, { id: this.item.customerId }, {
                                replace: true,
                                trigger: true
                            });
                        } else {
                            this.getItem(x.generatedId).then(() => {
                                resolve();
                            }, reject);
                        }
                    }
                    else {
                        resolve();
                    }
                });

                const customerDomainUpdateRequest = {
                    customerId: this.item.customerId,
                    allowedDomains: this.item.customerDomains.map(x => x.name)
                };

                this.customerService.updateCustomerAllowedDomains(customerDomainUpdateRequest).then(() => {
                    resolve();
                })

            }).catch(x => {
                if (x.validationResult && x.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(x.validationResult);
                } else {
                    this.notificationClientService.show(this.i18N.tr('failed-save-customer'), "", NotificationType.Error, 5000);
                }

                reject();
            });
        });
    }

    save() {        
        return new Promise<void>((resolve, reject) => {
            setTimeout(() => {
                Promise.all([
                    this.customerDueDiligenceSettings.updateSeniorApprovers(),
                    this.customerDueDiligenceSettings.updateComplianceCheckEmails(),
                    this.updateCustomer()
                ])
                .then(([updateComplianceCheckResponse, updateSeniorApproversResponse, updateCustomer]) => {
                    if (updateComplianceCheckResponse || updateSeniorApproversResponse || updateCustomer) {
                        this.notificationClientService.show(this.i18N.tr('changes-saved-successfully-text'), "", NotificationType.Success, 5000);
                        resolve();
                    }
                    else {
                        this.notificationClientService.show(this.i18N.tr('no-changes-performed-text'), this.i18N.tr('there-were-no-changes-to-save-text'), NotificationType.Info, 5000);
                        reject();
                    }
                })
                .catch((e) => {
                    this.notificationClientService.show(this.i18N.tr('failed-save-customer'), "", NotificationType.Error, 5000);
                    reject(e);
                });
            });
        })        
    }

    private resetTabState() {
        if (window.history && window.history.state && Object.keys(window.history.state).indexOf("current-tab") > -1) {
            delete window.history.state["current-tab"];
            window.history.replaceState(window.history.state, "Customer");
        }
    }

    private setTabState(tab: number, tabTitle: string) {
        window.history.state["current-tab"] = tab;
        window.history.replaceState(window.history.state, tabTitle);
    }

    private updateTabs() {
        const canAccessRules = this.userClaimsService.hasPermission(Claims.CanAccessCustomerRuleParameters);
        const canEditRules = this.userClaimsService.hasPermission(Claims.CanEditCustomerRuleParameters);

        this._tabs = [{
            lazyLoad: true,
            show: () => this.userClaimsService.hasPermission(Claims.CanAccessCustomers),
            title: this.i18N.tr("customer-details-title-text"),
            step: CustomerStepEnum.CustomerDetails,
            vm: this.customerBasicViewModel,
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    id: this.args.id,
                    customer: this.item
                };
            },
        },
        {
            title: "Subscriptions",
            step: CustomerStepEnum.CustomerSubscriptions,
            vm: this.customerSubscriptionsViewModel,
            lazyLoad: true,
            show: this.isExistingCustomer,
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    id: this.args.id,
                    customer: this.item
                };
            },
        },
        {
            lazyLoad: true,
            title: this.i18N.tr("business-unit-s-text"),
            step: CustomerStepEnum.BusinessUnits,
            vm: this.businessUnitViewModel,
            show: this.isExistingCustomer &&
                this.userClaimsService.hasPermission(Claims.CanViewAllBusinessUnits) &&
                this.userClaimsService.hasPermission(Claims.CanViewMyBusinessUnit) &&
                !this.item.isDataSubmission,
            onClick: () => {
                this.resetTabState();
                this.setTabState(CustomerStepEnum.BusinessUnits, this.i18N.tr("business-unit-s-text"))
                this.modelInCompose = {
                    id: this.args.id,
                    customer: this.item,
                    ruleConfigParameters: this.ruleConfigParameters,
                    onBusinessUnitSave: this.getItem
                };
            },
        },
        {
            lazyLoad: true,
            title: this.i18N.tr("roles-text"),
            step: CustomerStepEnum.ManageRoles,
            vm: this.customerRolesViewModel,
            show: this.isExistingCustomer,
            onClick: () => {
                this.resetTabState();
                this.customerRolesViewModel.branchId = this.branchId;
                this.modelInCompose = {
                    isDataSubmission: this.item.isDataSubmission
                };
            },
        },
        {
            lazyLoad: true,
            title: this.i18N.tr("rules-text"),
            step: CustomerStepEnum.ManageRules,
            vm: this.BusinessUnitRuleConfigsViewModel,
            show: this.userClaimsService.hasPermission(Claims.CanAccessCustomers)
                && this.userClaimsService.hasPermissions([Claims.CanEditRules, Claims.CanAccessRules], ClaimsRequireTypeEnum.RequireAny)
                && this.isExistingCustomer
                && !this.item.isDataSubmission,
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    branchId: this.branchId,
                    id: this.args.id,
                    customers: [this.item]
                };
            },
        },
        // {
        //     lazyLoad: true,
        //     title: "Risk Category Settings",
        //     step: CustomerStepEnum.RiskCategorySettings,
        //     vm: this.ruleRiskConfigs,
        //     show: this.userClaimsService.hasPermission(Claims.CanAccessCustomers) 
        //         && this.userClaimsService.hasPermission(Claims.CanEditCustomerRulesRiskSettings)
        //         && this.isExistingCustomer
        //         && !this.item.isDataSubmission,
        //     onClick: () => {
        //         this.resetTabState();
        //         this.modelInCompose = {
        //             id: this.args.id,
        //             level: RiskCategoryLevelEnum.Customer,
        //             isRisk:true
        //         };
        //     },
        // },
        {
            lazyLoad: true,
            title: this.i18N.tr("people-text"),
            step: CustomerStepEnum.ManagePeople,
            vm: this.viewPeople,
            show: this.isExistingCustomer &&
                (this.userClaimsService.hasPermission(Claims.CanViewAllPeople) || this.userClaimsService.hasPermission(Claims.CanViewPeopleInMyBusinessUnit)),
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    branchId: this.branchId,
                    filterByIsActive: true,
                    customerId: this.item.customerId,
                    customer: this.item,
                    canAddPerson: this.userClaimsService.hasPermission(Claims.CanAddPeopleCustomers),
                    canEditPerson: this.userClaimsService.hasPermission(Claims.CanEditPeopleCustomers)
                };
            }
        },
        // {
        //     lazyLoad: true,
        //     show: true,
        //     title: "Ports",
        //     step: 5,
        //     vm: this.ports,
        //     onClick: () => {
        //         this.resetTabState();
        //         this.modelInCompose = {
        //             customerId: this.item.customerId,
        //             customer: this.item
        //         };
        //     }
        // },
        {
            lazyLoad: true,
            show: () => this.isExistingCustomer && (canAccessRules || canEditRules) && !this.item.isDataSubmission,
            title: "History Trail",
            step: 6,
            vm: this.customerRuleHistoryTrail,
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    id: this.item.customerId
                };
            }
        },
        {
            lazyLoad: true,
            show: () => this.isExistingCustomer && (canAccessRules || canEditRules) && !this.item.isDataSubmission,
            title: this.i18N.tr("customer-rule-parameters-title-text"),
            step: CustomerStepEnum.CustomerRuleParameters,
            vm: this.customerRuleParameters,
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    id: this.args.id,
                    customer: this.item,
                    idsToInclude: this.parameterIdsUsed,
                    ruleConfigParameters: this.ruleConfigParameters,
                    isReadOnly: !canEditRules
                };
            },
        },
        {
            lazyLoad: true,
            show: () => this.isExistingCustomer && this.userClaimsService.hasPermission(Claims.CanViewCustomerCarbonAccountingSettings),
            title: "Carbon Accounting",
            step: 4,
            vm: this.emissionsPerformanceTarget,
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    customerId: this.item.customerId,
                    customer: this.item
                };
            }
        },
        {
            lazyLoad: true,
            title: "Shared Activities",
            step: CustomerStepEnum.ManageRules,
            vm: this.sharedActivitiesViewModel,
            show: this.userClaimsService.hasPermission(Claims.RightShipUser) && this.isExistingCustomer && !this.item.isDataSubmission,
            onClick: () => {
                this.modelInCompose = {
                    customerId: this.item.customerId
                };
            }
        },
        {
            lazyLoad: true,
            title: "Sanctions",
            step: CustomerStepEnum.CustomerSanctions,
            vm: this.customerSanctionsViewModel,
            show: this.userClaimsService.hasPermission(Claims.CanAccessSanctions),
            onClick: () => {
                this.resetTabState();
                this.modelInCompose = {
                    customerId: this.item.customerId,
                    customer: this.item
                };
            }
        },
        {
            lazyLoad: true,
            title: "RS Insights Subscription",
            step: CustomerStepEnum.CustomerReportsSettings,
            vm: this.customerReportsSettings,
            show: this.userClaimsService.hasPermission(Claims.RightShipUser) && this.isExistingCustomer && !this.item.isDataSubmission,
            onClick: () => {
                this.modelInCompose = {
                    customerId: this.item.customerId
                };
            }
        },
        {
            lazyLoad: true,
            title: "Customer Forms Setting (DMC)",
            step: CustomerStepEnum.CustomerFormsSetting,
            vm: this.customerFormsSetting,
            show: this.userClaimsService.hasPermission(Claims.CanAccessDocumentManagementSettingInCustomerTab),
            onClick: () => {
                this.modelInCompose = {
                    customerId: this.item.customerId,
                    customerFormTypes: this.item.customerFormTypes
                };
            }
        },
        {
            lazyLoad: true,
            title: "Due Diligence Configuration",
            step: CustomerStepEnum.CustomerDueDiligenceSettings,
            vm: this.customerDueDiligenceSettings,
            show: this.userClaimsService.hasPermission(Claims.CanAccessDueDiligenceConfigurationTab),
            onClick: () => {
                this.modelInCompose = {
                    customer: this.item,
                    showSaveButton: false
                };
            }
        }];
    }

    saveRisk() {
        return this.ruleRiskConfigs.save();
    }

    detached() {
        if (this.uow) {
            this.uow.dispose();
        }
        this.uow = null;
        this.signalService.unsubscribe(SignalFunctions.customerActiveStateChanged , this.onCustomerActiveStateChanged.bind(this));
    }

    protected showDelete() {
        const model = new RightshipConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');

        model.messages.push(this.i18N.tr('delete-entity-description', {
            name: this.item.name,
        }));

        model.title = this.i18N.tr('delete-entity-text', {
            name: this.item.name,
        });

        model.onConfirm = () => this.delete();
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
    }

    public delete() {
        this.customerService.deleteCustomer(this.item.customerId);
        this.notificationClientService.show("Deletion will be processed in the background", "", NotificationType.Info, 5000);
    }

    menuItems: MenuItem[] = [
        {
            click: () => {
                this.isExporting = true;
                this.customerService.downloadRulesExcel(this.item.customerId, null, this.item.name).then(() => {
                    this.isExporting = false;
                },  () => {
                    this.isExporting = false;
                })
            },
            isVisible: () => this.item.customerId != null && this.userClaimsService.hasPermission(Claims.CanAccessCustomerRuleParameters),
            item: {
                exporticon: true,
                text: 'Export rules'
            },
            classList: '',
        },
        {
            click: () => this.showDelete(),
            isVisible: () => this.canDelete && this.item.customerId != null,
            item: {
                deleteicon: true,
                text: 'Delete customer'
            },
            classList: '',
        }
    ];
}
